import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { CgProfile } from "react-icons/cg";
import { getProgramTargets } from "../../../utils/api/endpoints/reports/target";
import RequestedToByIcon from "../../../assets/icons/requested_to_by.svg";
import ScopeDisplay from "../../common/ScopeDisplay";
import { deriveAssetType } from "../../../utils/api/endpoints/reports/parseReports";
import { ProgramTarget } from "../../../utils/api/endpoints/programs/parsePrograms";
import { CVSS31 } from "../../../utils/cvss31/cvsscalc31";
import { parseCategoryString } from "../../../utils/categoryUtils";
import { parseCVSSVector } from "../../forms/inputs/SeverityScoreSelector";

interface RetestDetailsRightSectionProps {
  reportTitle: string;
  researcherUsername: string;
  researcherPfp: string;
  businessPfp: string;
  reportId: number;
  targetName: string;
  programName?: string;
  reportScope?: string;
  programId?: number;
  severityScore?: string;
  category?: string;
  assetName?: string;
  targetValues?: string[];
  endpointValues?: string[];
  targetEndpointMap?: Record<string, string[]>;
  targetMetadataMap?: Record<string, any>;
}

const RetestDetailsRightSection: React.FC<RetestDetailsRightSectionProps> = ({
  reportTitle,
  researcherUsername,
  researcherPfp,
  businessPfp,
  reportId,
  targetName,
  programName,
  reportScope,
  programId,
  severityScore,
  category,
  assetName,
  targetValues,
  endpointValues,
  targetEndpointMap,
  targetMetadataMap
}) => {
  const { score, category: derivedCategory } = useMemo(() => {
    if (severityScore && category && !severityScore.includes("CVSS")) {
      return { score: severityScore, category: category };
    }
    // Handle legacy or CVSS 4.0 case where score/category is embedded in a vector string
    if (severityScore && severityScore.includes("CVSS")) {
      try {
        const parsed = parseCVSSVector(severityScore);
        if (parsed && parsed.score && parsed.score !== "-.-") {
          return {
            score: parsed.score,
            category: parsed.category || category || "Info"
          };
        }
      } catch (e) {
        console.warn("Failed to parse severity vector:", e);
      }
    }
    return { score: severityScore || "0.0", category: category || "Info" };
  }, [severityScore, category]);

  const [assetCategory, setAssetCategory] = useState<string>(category || "Unknown");
  const [isScopeExpanded, setIsScopeExpanded] = useState(false);
  const [needsReadMore, setNeedsReadMore] = useState(false);
  const scopeRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scopeRef.current) {
      setNeedsReadMore(scopeRef.current.scrollHeight > 100);
    }
  }, [reportScope]);

  // Fetch asset category from program targets
  useEffect(() => {
    const fetchAssetCategory = async () => {
      // If we already have a specialized category that isn't from the report category, we might want to keep it
      // But usually we want to derive it from the targets for accuracy
      if (!programId || (!reportScope && (!targetValues || targetValues.length === 0))) return;

      try {
        const targetsResponse = await getProgramTargets(programId);
        const targets = (targetsResponse?.targets || []) as ProgramTarget[];

        const derivedType = deriveAssetType(
          reportScope,
          targets,
          targetValues
        );

        if (derivedType && derivedType !== "N/A" && derivedType !== "Unknown") {
          setAssetCategory(derivedType);
        }
      } catch (error) {
        console.warn("Failed to fetch program targets:", error);
      }
    };

    fetchAssetCategory();
  }, [programId, reportScope, targetValues]);

  // Parse the targetName to extract the URL with proper error handling
  const getTargetUrl = () => {
    if (reportScope) return reportScope;

    try {
      if (!targetName) return "N/A";
      const parsed = JSON.parse(targetName);
      if (!parsed || !Array.isArray(parsed) || parsed.length === 0) return "N/A";
      return parsed[0]?.targetName || "N/A";
    } catch (error) {
      // If not JSON, return as is
      return targetName || reportScope || "N/A";
    }
  };

  const targetUrl = getTargetUrl();

  return (
    <div className="w-full flex-shrink-0 flex flex-col gap-4">
      <div className="rounded-lg bg-white p-6 shadow-md">
        {/* Report Title */}
        <h2 className="mb-6 text-xl font-bold capitalize text-gray-900">{reportTitle}</h2>

        {/* Requested By */}
        <div className="mb-4 flex items-center gap-2">
          <img src={RequestedToByIcon} alt="Requested by" className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm text-gray-900 font-medium">Requested By:</span>
          <span className="text-sm text-gray-900 font-normal">{programName || "Business Owner"}</span>
        </div>

        {/* Assigned To */}
        <div className="mb-6 flex items-center gap-2">
          <img src={RequestedToByIcon} alt="Assigned to" className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm text-gray-900 font-medium">Assigned To:</span>
          {researcherPfp ? (
            <img
              src={researcherPfp}
              alt={researcherUsername}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <CgProfile size={24} className="text-gray-500" />
          )}
          <span className="text-sm text-gray-900 font-normal">{researcherUsername || "N/A"}</span>
        </div>

        {/* Border */}
        <div className="mb-6 border-t border-gray-200"></div>

        {/* Action Asset Information */}
        <div className="mb-6">
          <p className="mb-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Action Asset Details</p>
          <div className="bg-slate-50 border border-slate-100 rounded-xl overflow-hidden p-3">
            <ScopeDisplay
              scope={reportScope || targetUrl}
              assetName={assetName}
              targetValues={targetValues}
              endpointValues={endpointValues}
              targetEndpointMap={targetEndpointMap}
              targetMetadataMap={targetMetadataMap}
              programAssetName={programName}
              theme="light"
              showLabel={false}
            />
          </div>

          {/* Asset Category */}
          <div className="mb-4">
            <p className="mb-2 text-sm text-gray-600 font-medium">Asset Category</p>
            <div className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg inline-block">
              <span className="text-sm font-semibold text-slate-700">
                { parseCategoryString((assetCategory && assetCategory !== "Unknown") ? assetCategory : (category || "Unknown")) }
              </span>
            </div>
          </div>

          {/* Severity Score */}
          <div className="mb-6">
            <p className="mb-2 text-sm text-gray-600">Severity Score</p>
            <span className="block text-base font-medium text-gray-900">
              {score} ({derivedCategory})
            </span>
          </div>

          {/* View Report Button */}
          <div className="mt-auto pt-4">
            <Link
              to={`/dashboard/reports/${reportId}`}
              className="block w-full rounded-lg px-4 py-2 text-center font-medium transition-colors"
              style={{ background: '#010D2C', color: '#58CC02' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#232f53')}
              onMouseLeave={e => (e.currentTarget.style.background = '#010D2C')}
            >
              View Report
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetestDetailsRightSection;
